import {
    pointerWithin,
    type CollisionDetection,
    type UniqueIdentifier,
} from "@dnd-kit/core";
import type { DraftRefsTarget } from "../useDraftRefsByTarget";

export const COMPOSER_DROP_ZONE_IDS: Record<DraftRefsTarget, string> = {
    main: "chat-drop-zone:main",
    subthread: "chat-drop-zone:subthread",
};
export const CHAT_TAB_DROP_ZONE_ID = "chat-drop-zone:chat-tab";
export const PANE_TOGGLE_DROP_ZONE_ID = "chat-drop-zone:pane-toggle";

export function composerTargetForZone(
    zoneId: UniqueIdentifier,
): DraftRefsTarget | null {
    if (zoneId === COMPOSER_DROP_ZONE_IDS.main) {
        return "main";
    }
    if (zoneId === COMPOSER_DROP_ZONE_IDS.subthread) {
        return "subthread";
    }
    return null;
}

interface DropZoneHit<TId> {
    id: TId;
    rect: { width: number; height: number };
}

/**
 * Picks the zone a drag is over from the zones under the pointer: the
 * smallest one wins, so an open subthread beats the chat pane around it,
 * and the subthread also wins a tie, since its panel can cover the whole pane.
 * Zero-size zones (hidden tab panels, a collapsed pane) never win.
 */
export function pickDropTarget<TId extends UniqueIdentifier>(
    hits: Array<DropZoneHit<TId>>,
): TId | null {
    let winner: DropZoneHit<TId> | null = null;
    for (const hit of hits) {
        const area = hit.rect.width * hit.rect.height;
        if (area <= 0) {
            continue;
        }
        const winnerArea = winner
            ? winner.rect.width * winner.rect.height
            : Infinity;
        const isSubthreadTie =
            area === winnerArea && hit.id === COMPOSER_DROP_ZONE_IDS.subthread;
        if (area < winnerArea || isSubthreadTie) {
            winner = hit;
        }
    }
    return winner?.id ?? null;
}

/** dnd-kit collision detection that keeps only `pickDropTarget`'s winner. */
export const smallestZoneUnderPointer: CollisionDetection = (args) => {
    const collisions = pointerWithin(args);
    const winnerId = pickDropTarget(
        collisions.flatMap((collision) => {
            const rect = args.droppableRects.get(collision.id);
            return rect ? [{ id: collision.id, rect }] : [];
        }),
    );
    return collisions.filter((collision) => collision.id === winnerId);
};
